import classNames from 'classnames';
import * as React from 'react';
import { useIntl } from 'react-intl';
import StarRating from '../../../../../../atoms/icons/StarRating';
import MerchantTag from '../../../../../../molecules/merchant/common/tags/MerchantTag';
import LowestPrice from '../../../../../../molecules/merchant/common/texts/LowestPrice';
import {
  BookingPageScheme,
  BookingPageTagName,
} from '../../../../../../domain/entities/BookingPage';
import MerchantCourseDetailsReservationButton from '../../../../../../molecules/merchant/resources/book/MerchantCourseDetailsReservationButton';

import css from './MerchantCourseDetailsHeader.module.scss';
import { MerchantSetting } from '../../../../../../interfaces/merchants';
import { RSButtonLink } from '../../../../../../reservation-page/RSButtonLink';
import KantanIcon from '../../../../../../reservation-page/icons/kantan-icon.svg';
import { useUserMe } from '../../../../../../hooks/api/me';
import { RSKantanIconButtonAnchor } from '../../../../../../reservation-page/RSKantanIconButtonAnchor';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import {
  EventSchemeResource,
  CourseSchemeResource,
} from 'src/interfaces/reservation_flow';

export interface OwnProps {
  merchantSetting: MerchantSetting;
  isBookable: boolean;
  model_type: BookingPageScheme;
  isFullyBooked: boolean;
  name: string;
  nickname: string;
  merchant_public_id: string;
  public_id: string;
  tag_names: BookingPageTagName[];
  rate_count: number;
  rate?: number;
  lowest_price?: number;
  unique_pricing: boolean;
  isV2Flow: boolean;
  isRequireCustomerLogin: boolean;
  contact_url?: string;
  resource: EventSchemeResource | CourseSchemeResource | undefined;
  newReservationFlowPath: string;
}

type Props = OwnProps;

const MerchantCourseDetailsHeader: React.FC<Props> = (props) => {
  const {
    merchantSetting,
    isFullyBooked,
    name,
    tag_names,
    rate_count,
    rate,
    lowest_price,
    unique_pricing,
    isV2Flow,
    merchant_public_id,
    public_id: resource_public_id,
    contact_url,
    resource,
    model_type,
    newReservationFlowPath,
  } = props;
  const classes = classNames(css.MerchantCourseDetailsHeader);
  const intl = useIntl();
  const { user } = useUserMe();

  const isEvent = model_type === BookingPageScheme.Event;
  const isCourse =
    model_type === BookingPageScheme.Availability ||
    model_type === BookingPageScheme.Assign;

  const NewReservationFlowButton = React.useMemo(() => {
    if (resource && !resource?.is_reservable)
      return (
        <div className="w-[200px]">
          <RSButtonAnchor
            isBlock={true}
            variant="outline"
            href={
              contact_url ||
              `/${merchant_public_id}/contact?via=${resource_public_id}`
            }
          >
            お問い合わせ
          </RSButtonAnchor>
        </div>
      );
    if (
      !resource ||
      (user && resource?.acceptable_customer_login_status !== 'guest')
    ) {
      return (
        <div className="w-[200px]">
          <RSButtonLink isBlock={true} href={newReservationFlowPath}>
            {intl.formatMessage({
              id: 'merchants.bookingPages.bookReservation',
            })}
          </RSButtonLink>
        </div>
      );
    }
    return (
      <div className="flex gap-1.5">
        {resource?.acceptable_customer_login_status === 'all' && (
          <div className="w-[200px]">
            <RSButtonLink isBlock={true} href={newReservationFlowPath}>
              {intl.formatMessage({
                id: 'merchants.bookingPages.bookGuestReservation',
              })}
            </RSButtonLink>
          </div>
        )}
        {resource?.acceptable_customer_login_status === 'guest' && (
          <div className="w-[200px]">
            <RSButtonLink isBlock={true} href={newReservationFlowPath}>
              {intl.formatMessage({
                id: 'merchants.bookingPages.bookReservation',
              })}
            </RSButtonLink>
          </div>
        )}
        {resource?.acceptable_customer_login_status !== 'guest' && (
          <div className="w-[200px]">
            <RSKantanIconButtonAnchor
              icon={<KantanIcon width="24" height="24" viewBox="0 0 24 24" />}
              isBlock={true}
              href={`/signin/user?return_to=${newReservationFlowPath}`}
              variant="black"
            >
              {intl.formatMessage({
                id: 'merchants.bookingPages.bookReservationWithReservation',
              })}
            </RSKantanIconButtonAnchor>
          </div>
        )}
      </div>
    );
  }, [
    contact_url,
    intl,
    merchant_public_id,
    newReservationFlowPath,
    resource,
    resource_public_id,
    user,
  ]);

  return (
    <div className={classes}>
      <div className={css.MerchantCourseDetailsHeader__Inner}>
        {tag_names.map((tag, i) => (
          <MerchantTag key={i} status={tag} />
        ))}
        <div className={css.MerchantCourseDetailsHeader__Header}>
          <h1 className={css.MerchantCourseDetailsHeader__Title}>{name}</h1>
          <div className={css.MerchantCourseDetailsHeader__SummaryContainer}>
            <div className={css.MerchantCourseDetailsHeader__Summary}>
              <span>
                {merchantSetting.is_customer_review_active && (
                  <>
                    <StarRating
                      className={css.MerchantCourseDetailsHeader__StarRating}
                      rate={rate || 0}
                    />
                    {rate_count > 0 && (
                      <span
                        className={css.MerchantCourseDetailsHeader__ReviewCount}
                      >
                        {`(${rate_count})`}
                      </span>
                    )}
                  </>
                )}
              </span>
              {lowest_price !== undefined && !isV2Flow && (
                <LowestPrice
                  className={css.MerchantCourseDetailsHeader__Price}
                  lowestPrice={lowest_price}
                  uniquePricing={unique_pricing}
                />
              )}
            </div>

            <div className={css.MerchantCourseDetailsHeader__ButtonContainer}>
              {isV2Flow && (isEvent || isCourse) ? (
                <div className="legacyPc:block hidden">
                  {NewReservationFlowButton}
                </div>
              ) : (
                <div className="flex justify-end">
                  <div className="w-[200px]">
                    <MerchantCourseDetailsReservationButton
                      contactUrl={merchantSetting.contact_url}
                      {...props}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {isFullyBooked && (
          <p className={css.MerchantCourseDetailsHeader__Notice}>
            {intl.formatMessage({
              id: 'merchants.bookingPages.notice.fullyBooked',
            })}
          </p>
        )}
      </div>
    </div>
  );
};

export default MerchantCourseDetailsHeader;
